import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import {
  HELP_VIDEO_DEFAULTS,
  type HelpTopic,
  type HelpVideo,
} from '../components/help/helpVideos';
import { fetchHelpVideos } from '../services/helpVideos';

// ─────────────────────────────────────────────────────────────────────────────
// HelpVideoContext — the single shared "remote control" for the tutorial box,
// and the cache of what each tutorial currently points at.
//
// The video box itself is mounted ONCE at the app root (see App.tsx), OUTSIDE
// the page routes, so it never unmounts when the user navigates. Each tab's
// "How to use" button simply calls `openHelpVideo('estimates')` to point that
// one box at a different video. That's why the box survives going from
// Estimates → pick client → pick job → estimate builder without disappearing.
//
// WHERE THE VIDEOS COME FROM
// Rows in `help_videos`, uploaded from the platform-admin panel. They are
// fetched LAZILY — the first time anyone opens a tutorial, not on page load —
// because most sessions never click "How to use", and the app's cold start is
// already carrying auth, settings and workspace resolution.
//
// The fetch failing is not an error state here. A broken tutorial list must
// never break the page it floats over, so any failure falls back to the static
// titles in helpVideos.ts and the box shows its "coming soon" placeholder —
// exactly what it showed before this table existed.
// ─────────────────────────────────────────────────────────────────────────────

interface HelpVideoContextType {
  /** Whether the floating tutorial box is currently open. */
  isOpen: boolean;
  /** Which tutorial is loaded, or null when closed. */
  topic: HelpTopic | null;
  /** Open the box on a given tutorial (or switch the open box to it). */
  openHelpVideo: (topic: HelpTopic) => void;
  /** Hide the box (stops playback). */
  closeHelpVideo: () => void;
  /** The uploaded video for a topic, or the built-in default if none/not loaded. */
  getVideo: (topic: HelpTopic) => HelpVideo;
  /** True while the first fetch is in flight — the box shows a spinner, not
   *  "coming soon", so a slow network doesn't look like a missing video. */
  loading: boolean;
}
// Deliberately no `refresh()`. It would only matter if someone could publish a
// video and watch a tutorial in the same session, and they cannot: a platform
// admin gets an isolated shell (App.tsx) with no HelpVideoBox in it, and nobody
// else can upload. Shipping an unused refresher would mean maintaining its
// double-fetch and last-write-wins edge cases for a path no user can take.

const HelpVideoContext = createContext<HelpVideoContextType | undefined>(undefined);

export const useHelpVideo = (): HelpVideoContextType => {
  const ctx = useContext(HelpVideoContext);
  if (!ctx) throw new Error('useHelpVideo must be used within HelpVideoProvider');
  return ctx;
};

export const HelpVideoProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [topic, setTopic] = useState<HelpTopic | null>(null);
  const [videos, setVideos] = useState<Partial<Record<HelpTopic, HelpVideo>>>({});
  const [loading, setLoading] = useState(false);

  // Guards against a second fetch while the first is still in flight — without
  // it, clicking two different "How to use" buttons quickly fires two queries.
  const inFlight = useRef<Promise<void> | null>(null);
  const hasLoaded = useRef(false);

  const load = useCallback(async (): Promise<void> => {
    if (inFlight.current) return inFlight.current;

    setLoading(true);
    const run = (async () => {
      try {
        const rows = await fetchHelpVideos();
        const next: Partial<Record<HelpTopic, HelpVideo>> = {};
        for (const r of rows) {
          next[r.topic] = {
            // An empty title in the DB should not blank the box header.
            title: r.title || HELP_VIDEO_DEFAULTS[r.topic]?.title || '',
            src: r.src,
            poster: r.poster ?? undefined,
          };
        }
        setVideos(next);
        hasLoaded.current = true;
      } catch (err) {
        // Deliberately non-fatal. The box falls back to defaults and shows
        // "coming soon" — the same thing it showed before the table existed.
        console.warn('[HelpVideo] could not load tutorials:', (err as Error).message);
      } finally {
        setLoading(false);
        inFlight.current = null;
      }
    })();

    inFlight.current = run;
    return run;
  }, []);

  const openHelpVideo = useCallback((next: HelpTopic) => {
    setTopic(next);
    if (!hasLoaded.current) void load();
  }, [load]);

  const closeHelpVideo = useCallback(() => {
    setTopic(null);
  }, []);

  const getVideo = useCallback(
    (which: HelpTopic): HelpVideo => videos[which] ?? HELP_VIDEO_DEFAULTS[which],
    [videos],
  );

  const value = React.useMemo(
    () => ({
      isOpen: topic !== null,
      topic,
      openHelpVideo,
      closeHelpVideo,
      getVideo,
      loading,
    }),
    [topic, openHelpVideo, closeHelpVideo, getVideo, loading],
  );

  return <HelpVideoContext.Provider value={value}>{children}</HelpVideoContext.Provider>;
};
